// ── useRemoteSnapshot — Remote snapshot browsing & delta comparison ──────
//
// Mirrors useScopeSelection but data-sourced from a REMOTE BFF.
// Manages: snapshot capture, namespace browsing, scope-key fetching,
// delta annotations (new/synced/changed/local_only), and selection
// of remote scopes for pulling.
//
// State machine:
//   idle → loading_namespaces → ready → (browse ns → load_scopes) → selected
//
import { useCallback, useMemo, useState } from 'react';
import {
  fetchSnapshotNamespaces,
  fetchSnapshotScopes,
  diagnoseRemote,
  type SnapshotNamespace,
  type SnapshotScope,
  type SnapshotNamespaceSummary,
  type SnapshotScopeDelta,
  type SnapshotNamespacesResult,
  type SnapshotScopesResult,
  type RemoteHealthResult,
} from '../../../api/snapshotApi';

export type SnapshotPhase =
  | 'idle'
  | 'loading_namespaces'
  | 'ready'
  | 'error';

export interface UseRemoteSnapshotReturn {
  // ── Phase & loading ───────────────────────────
  phase: SnapshotPhase;
  error: string | null;
  errorCode: string | null;

  // ── Snapshot data ─────────────────────────────
  snapshotId: string | null;
  capturedAt: string | null;
  namespaces: SnapshotNamespace[];
  localOnlyNamespaces: SnapshotNamespace[];
  namespaceSummary: SnapshotNamespaceSummary | null;

  // ── Namespace browsing ────────────────────────
  selectedNs: string | null;
  setSelectedNs: (ns: string | null) => void;
  nsSearch: string;
  setNsSearch: (search: string) => void;
  filteredNamespaces: SnapshotNamespace[];

  // ── Scope keys for selected namespace ─────────
  scopeKeys: SnapshotScope[];
  localOnlyScopes: SnapshotScope[];
  scopeDelta: SnapshotScopeDelta | null;
  scopesLoading: boolean;
  keySearch: string;
  setKeySearch: (search: string) => void;
  filteredScopes: SnapshotScope[];

  // ── Selection ─────────────────────────────────
  selectedScopeIds: Set<number>;
  toggleScopeId: (id: number) => void;
  toggleAllScopes: () => void;
  toggleNamespaceNewScopes: (ns: string) => void;
  selectAllNew: () => void;
  allFilteredSelected: boolean;
  someFilteredSelected: boolean;

  // ── Cached scope data per namespace ───────────
  nsScopeCache: Map<string, SnapshotScope[]>;

  // ── Actions ───────────────────────────────────
  captureSnapshot: () => Promise<void>;
  clearSnapshot: () => void;
  runDiagnostics: () => Promise<void>;

  // ── Diagnostics ───────────────────────────────
  diagnostics: RemoteHealthResult | null;
  diagnosing: boolean;

  // ── Derived ───────────────────────────────────
  selectedNewCount: number;
  selectedSyncedCount: number;
  totalSelectedCount: number;
}

export function useRemoteSnapshot({
  remoteUrl,
  sourceEnv,
  tenantId,
  accessToken,
  onRefreshAuth,
}: {
  remoteUrl: string | null;
  sourceEnv: string;
  tenantId: number | '' | undefined;
  accessToken: string | null;
  /** Called on auth failure — should refresh the token and return a new one (or null). */
  onRefreshAuth?: () => Promise<string | null>;
}): UseRemoteSnapshotReturn {
  const tid = tenantId !== '' && tenantId !== undefined ? tenantId : undefined;

  // ── Core state ────────────────────────────────────────────────────
  const [phase, setPhase] = useState<SnapshotPhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  // Diagnostics state
  const [diagnostics, setDiagnostics] = useState<RemoteHealthResult | null>(null);
  const [diagnosing, setDiagnosing] = useState(false);

  // Namespace snapshot
  const [snapshotId, setSnapshotId] = useState<string | null>(null);
  const [capturedAt, setCapturedAt] = useState<string | null>(null);
  const [namespaces, setNamespaces] = useState<SnapshotNamespace[]>([]);
  const [localOnlyNamespaces, setLocalOnlyNamespaces] = useState<SnapshotNamespace[]>([]);
  const [namespaceSummary, setNamespaceSummary] = useState<SnapshotNamespaceSummary | null>(null);

  // Browsing state
  const [selectedNs, setSelectedNs] = useState<string | null>(null);
  const [nsSearch, setNsSearch] = useState('');
  const [keySearch, setKeySearch] = useState('');

  // Scope keys for current namespace
  const [scopeKeys, setScopeKeys] = useState<SnapshotScope[]>([]);
  const [localOnlyScopes, setLocalOnlyScopes] = useState<SnapshotScope[]>([]);
  const [scopeDelta, setScopeDelta] = useState<SnapshotScopeDelta | null>(null);
  const [scopesLoading, setScopesLoading] = useState(false);

  // Scope data cache per namespace
  const [nsScopeCache, setNsScopeCache] = useState<Map<string, SnapshotScope[]>>(new Map());

  // Selection
  const [selectedScopeIds, setSelectedScopeIds] = useState<Set<number>>(new Set());

  // ── Capture snapshot (namespace listing) ──────────────────────────

  const captureSnapshot = useCallback(async () => {
    if (!remoteUrl || !sourceEnv) return;
    setPhase('loading_namespaces');
    setError(null);
    setErrorCode(null);

    const doCapture = async (token: string | null | undefined) => {
      return fetchSnapshotNamespaces({
        remote_url: remoteUrl,
        source_env: sourceEnv,
        tenant_id: tid,
        access_token: token ?? undefined,
      });
    };

    const applyResult = (result: SnapshotNamespacesResult) => {
      setSnapshotId(result.id);
      setCapturedAt(result.captured_at);
      setNamespaces(result.namespaces);
      setLocalOnlyNamespaces(result.local_only_namespaces);
      setNamespaceSummary(result.summary);
      setPhase('ready');
      setSelectedNs(null);
      setScopeKeys([]);
      setLocalOnlyScopes([]);
      setScopeDelta(null);
      setNsScopeCache(new Map());
      setSelectedScopeIds(new Set());
    };

    try {
      const result = await doCapture(accessToken);
      applyResult(result);
    } catch (err: any) {
      const body = err?.body;
      const code = (typeof body === 'object' && body?.code) || null;

      // ── Auto-retry: on auth failure, refresh token and retry once ──
      if ((code === 'remote_auth_failed' || code === 'no_auth') && onRefreshAuth) {
        try {
          const freshToken = await onRefreshAuth();
          if (freshToken) {
            const retryResult = await doCapture(freshToken);
            applyResult(retryResult);
            return; // Success on retry — done.
          }
        } catch {
          // Retry also failed — fall through to show error.
        }
      }

      const msg = (typeof body === 'object' && body?.message) || err?.message || 'Snapshot capture failed';
      setError(msg);
      setErrorCode(code);
      setPhase('error');

      // Auto-run diagnostics on failure to give actionable feedback.
      if (remoteUrl) {
        setDiagnosing(true);
        diagnoseRemote({
          remote_url: remoteUrl,
          source_env: sourceEnv,
          tenant_id: tid,
          access_token: accessToken ?? undefined,
        }).then(result => {
          setDiagnostics(result);
        }).catch(() => {
          // Diagnostics itself failed — BFF unreachable, ignore.
        }).finally(() => setDiagnosing(false));
      }
    }
  }, [remoteUrl, sourceEnv, tid, accessToken, onRefreshAuth]);

  // ── Browse namespace (fetch scope keys on demand) ─────────────────

  const handleSelectNs = useCallback(async (ns: string | null) => {
    setSelectedNs(ns);
    setKeySearch('');
    if (!ns) {
      setScopeKeys([]);
      setLocalOnlyScopes([]);
      setScopeDelta(null);
      return;
    }

    // Check cache first
    const cached = nsScopeCache.get(ns);
    if (cached) {
      setScopeKeys(cached);
      // Recompute delta summary from cached data
      const newC = cached.filter(s => s.delta === 'new').length;
      const syncedC = cached.filter(s => s.delta === 'synced').length;
      setScopeDelta({ newCount: newC, syncedCount: syncedC, localOnlyCount: 0, totalRemote: cached.length });
      setLocalOnlyScopes([]);
      return;
    }

    if (!remoteUrl) return;
    setScopesLoading(true);
    try {
      const result: SnapshotScopesResult = await fetchSnapshotScopes({
        remote_url: remoteUrl,
        source_env: sourceEnv,
        tenant_id: tid,
        access_token: accessToken ?? undefined,
        scope_namespace: ns,
      });
      setScopeKeys(result.scopes);
      setLocalOnlyScopes(result.local_only);
      setScopeDelta(result.summary);

      // Cache
      setNsScopeCache(prev => {
        const next = new Map(prev);
        next.set(ns, result.scopes);
        return next;
      });
    } catch {
      // On error, show empty
      setScopeKeys([]);
      setLocalOnlyScopes([]);
      setScopeDelta(null);
    } finally {
      setScopesLoading(false);
    }
  }, [remoteUrl, sourceEnv, tid, accessToken, nsScopeCache]);

  // ── Filtered lists ────────────────────────────────────────────────

  const filteredNamespaces = useMemo(() => {
    const term = nsSearch.trim().toLowerCase();
    if (!term) return namespaces;
    return namespaces.filter(n => n.scope_namespace.toLowerCase().includes(term));
  }, [namespaces, nsSearch]);

  const filteredScopes = useMemo(() => {
    const term = keySearch.trim().toLowerCase();
    if (!term) return scopeKeys;
    return scopeKeys.filter(s =>
      s.scope_key.toLowerCase().includes(term) ||
      s.scope_namespace.toLowerCase().includes(term),
    );
  }, [scopeKeys, keySearch]);

  // ── Selection toggles ────────────────────────────────────────────

  const toggleScopeId = useCallback((id: number) => {
    setSelectedScopeIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAllScopes = useCallback(() => {
    const allIds = filteredScopes
      .map(s => s.id_scope)
      .filter((id): id is number => id != null);

    setSelectedScopeIds(prev => {
      const next = new Set(prev);
      const allSelected = allIds.length > 0 && allIds.every(id => next.has(id));
      if (allSelected) {
        allIds.forEach(id => next.delete(id));
      } else {
        allIds.forEach(id => next.add(id));
      }
      return next;
    });
  }, [filteredScopes]);

  const toggleNamespaceNewScopes = useCallback((ns: string) => {
    const cached = nsScopeCache.get(ns);
    if (!cached) return;
    const newIds = cached
      .filter(s => s.delta === 'new' && s.id_scope != null)
      .map(s => s.id_scope!);

    if (newIds.length === 0) return;
    setSelectedScopeIds(prev => {
      const next = new Set(prev);
      const allSelected = newIds.every(id => next.has(id));
      if (allSelected) {
        newIds.forEach(id => next.delete(id));
      } else {
        newIds.forEach(id => next.add(id));
      }
      return next;
    });
  }, [nsScopeCache]);

  const selectAllNew = useCallback(() => {
    // Select all "new" scopes across all cached namespaces
    const newIds: number[] = [];
    nsScopeCache.forEach(scopes => {
      for (const s of scopes) {
        if (s.delta === 'new' && s.id_scope != null) newIds.push(s.id_scope);
      }
    });
    setSelectedScopeIds(prev => {
      const next = new Set(prev);
      newIds.forEach(id => next.add(id));
      return next;
    });
  }, [nsScopeCache]);

  // ── Derived state ─────────────────────────────────────────────────

  const allFilteredSelected =
    filteredScopes.length > 0 &&
    filteredScopes.every(s => s.id_scope != null && selectedScopeIds.has(s.id_scope));

  const someFilteredSelected =
    !allFilteredSelected &&
    filteredScopes.length > 0 &&
    filteredScopes.some(s => s.id_scope != null && selectedScopeIds.has(s.id_scope));

  const selectedNewCount = useMemo(() => {
    let count = 0;
    nsScopeCache.forEach(scopes => {
      for (const s of scopes) {
        if (s.delta === 'new' && s.id_scope != null && selectedScopeIds.has(s.id_scope)) count++;
      }
    });
    return count;
  }, [nsScopeCache, selectedScopeIds]);

  const selectedSyncedCount = useMemo(() => {
    let count = 0;
    nsScopeCache.forEach(scopes => {
      for (const s of scopes) {
        if (s.delta === 'synced' && s.id_scope != null && selectedScopeIds.has(s.id_scope)) count++;
      }
    });
    return count;
  }, [nsScopeCache, selectedScopeIds]);

  // ── Manual diagnostics ────────────────────────────────────────────

  const runDiagnostics = useCallback(async () => {
    if (!remoteUrl) return;
    setDiagnosing(true);
    setDiagnostics(null);
    try {
      const result = await diagnoseRemote({
        remote_url: remoteUrl,
        source_env: sourceEnv,
        tenant_id: tid,
        access_token: accessToken ?? undefined,
      });
      setDiagnostics(result);
    } catch {
      // BFF itself unreachable — can't diagnose.
    } finally {
      setDiagnosing(false);
    }
  }, [remoteUrl, sourceEnv, tid, accessToken]);

  // ── Clear ─────────────────────────────────────────────────────────

  const clearSnapshot = useCallback(() => {
    setPhase('idle');
    setError(null);
    setErrorCode(null);
    setDiagnostics(null);
    setDiagnosing(false);
    setSnapshotId(null);
    setCapturedAt(null);
    setNamespaces([]);
    setLocalOnlyNamespaces([]);
    setNamespaceSummary(null);
    setSelectedNs(null);
    setScopeKeys([]);
    setLocalOnlyScopes([]);
    setScopeDelta(null);
    setNsScopeCache(new Map());
    setSelectedScopeIds(new Set());
    setNsSearch('');
    setKeySearch('');
  }, []);

  return {
    phase,
    error,
    errorCode,
    snapshotId,
    capturedAt,
    namespaces,
    localOnlyNamespaces,
    namespaceSummary,
    selectedNs,
    setSelectedNs: handleSelectNs,
    nsSearch,
    setNsSearch,
    filteredNamespaces,
    scopeKeys,
    localOnlyScopes,
    scopeDelta,
    scopesLoading,
    keySearch,
    setKeySearch,
    filteredScopes,
    selectedScopeIds,
    toggleScopeId,
    toggleAllScopes,
    toggleNamespaceNewScopes,
    selectAllNew,
    allFilteredSelected,
    someFilteredSelected,
    nsScopeCache,
    captureSnapshot,
    clearSnapshot,
    runDiagnostics,
    diagnostics,
    diagnosing,
    selectedNewCount,
    selectedSyncedCount,
    totalSelectedCount: selectedScopeIds.size,
  };
}
